/**
 * Manual Delivery Detection Utility
 *
 * Identifies products that require manual delivery based on their category.
 * These are products like Stecklinge (cuttings), Sporenspritzen (spore syringes),
 * Samen (seeds), and Erde (soil) that cannot be shipped via standard carriers.
 */

export interface CartItem {
  id?: string
  product: any
  quantity?: number
  variant?: any
}

export interface ManualDeliveryResult {
  containsManualDelivery: boolean
  manualDeliveryItems: CartItem[]
  regularItems: CartItem[]
  manualDeliveryProductNames: string[]
}

/**
 * Category titles/slugs that indicate a product requires manual delivery.
 * These should match the category names/slugs in your CMS.
 *
 * Includes:
 * - Cannabis Stecklinge / Stecklinge (cuttings)
 * - Sporenspritze (spore syringes)
 * - Samen (seeds)
 * - Erde (soil/growing medium) - Added per user request
 * - Salvia Divinorum - Added per user request
 */
export const MANUAL_DELIVERY_CATEGORIES = [
  // Stecklinge (cuttings)
  'Cannabis Stecklinge',
  'cannabis-stecklinge',
  'Stecklinge',
  'stecklinge',
  // Spore syringes
  'Sporenspritze',
  'sporenspritze',
  // Seeds
  'Samen',
  'samen',
  // Soil/growing medium (added per user request)
  'Erde',
  'erde',
  // Plants (added per user request)
  'Salvia Divinorum',
  'salvia-divinorum',
  'salvia divinorum',
  'Salvia Divinorum Pflanze',
  'salvia-divinorum-pflanze',
  'salvia divinorum pflanze',
]

/**
 * Checks if a product belongs to a manual delivery category.
 * Looks at both category title and slug for matching.
 *
 * @param product The product to check.
 * @returns True if the product requires manual delivery, false otherwise.
 */
export function isManualDeliveryProduct(product: any): boolean {
  console.log('Checking product for manual delivery:', {
    productTitle: product?.title,
    productId: product?.id,
    hasCategories: !!product?.categories,
    categoriesIsArray: Array.isArray(product?.categories),
    categoriesCount: product?.categories?.length,
    categories: product?.categories,
  })

  if (!product?.categories || !Array.isArray(product.categories)) {
    console.log('  -> No categories or not array, returning false')
    return false
  }

  const result = product.categories.some((category: any) => {
    console.log('  Checking category:', {
      categoryType: typeof category,
      category: category,
    })

    // Handle both populated (object) and unpopulated (id only) categories
    if (typeof category === 'object' && category !== null) {
      const title = category.title?.toLowerCase()
      const slug = category.slug?.toLowerCase()

      const isMatch = MANUAL_DELIVERY_CATEGORIES.some((cat) => {
        const catLower = cat.toLowerCase()
        const matches = title === catLower || slug === catLower
        if (matches) {
          console.log('    -> MATCH FOUND:', { title, slug, matchedAgainst: catLower })
        }
        return matches
      })

      return isMatch
    }
    return false
  })

  console.log('  -> Final result:', result)
  return result
}

/**
 * Analyzes a shopping cart to identify items that require manual delivery.
 *
 * @param cartItems The items in the shopping cart.
 * @returns An object containing:
 *   - containsManualDelivery: boolean flag
 *   - manualDeliveryItems: array of items requiring manual delivery
 *   - regularItems: array of items that can be shipped normally
 *   - manualDeliveryProductNames: array of product names for display
 */
export function analyzeCartForManualDelivery(cartItems: CartItem[]): ManualDeliveryResult {
  const manualDeliveryItems: CartItem[] = []
  const regularItems: CartItem[] = []
  const manualDeliveryProductNames: string[] = []

  if (!cartItems || !Array.isArray(cartItems)) {
    return {
      containsManualDelivery: false,
      manualDeliveryItems: [],
      regularItems: [],
      manualDeliveryProductNames: [],
    }
  }

  cartItems.forEach((item) => {
    const product = item.product

    if (typeof product === 'object' && product && isManualDeliveryProduct(product)) {
      manualDeliveryItems.push(item)
      if (product.title) {
        manualDeliveryProductNames.push(product.title)
      }
    } else if (product) {
      regularItems.push(item)
    }
  })

  return {
    containsManualDelivery: manualDeliveryItems.length > 0,
    manualDeliveryItems,
    regularItems,
    manualDeliveryProductNames,
  }
}

/**
 * Quick check if cart contains any manual delivery items.
 * Use this for simple boolean checks without needing the full breakdown.
 *
 * @param cartItems The items in the shopping cart.
 * @returns True if cart contains manual delivery items.
 */
export function hasManualDeliveryItems(cartItems: CartItem[]): boolean {
  if (!cartItems || !Array.isArray(cartItems)) return false

  return cartItems.some((item) => {
    const product = item.product
    return typeof product === 'object' && product && isManualDeliveryProduct(product)
  })
}
